// ── HISTORICAL COST & STOCK EXCEPTIONS ─────────────────────────────────────
// A read-only diagnostic that reconciles what was RECORDED against what the
// chronological inventory replay implies, so an operator can resolve bad rows
// WITHOUT silently rewriting history. It never mutates records; it only reports.
//
// Three exception families, emitted as one uniform row list:
//   • cost   — a sale line whose recorded unit cost differs from the replay
//              (as-of-date) WAC. reason: 'zero_cost_snapshot' (recorded 0 while a
//              real cost basis exists) or 'wac_variation' (non-zero but drifted).
//   • return — a customer return that is orphaned, dated before its own sale, or
//              exceeds the quantity still returnable on its parent sale.
//   • stock  — a product whose stored balance ≠ the quantity implied by history.
//
// Pure: no React, no storage. Locked down by costAudit.test.js.

import { computeWAC, normProductName } from './moneyMath';
import { reconcileProductStock } from './inventory';

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const hasId = (v) => v !== undefined && v !== null && v !== '';
const r2 = (v) => Math.round(v * 100) / 100;
const dkey = (d) => String(d || '').slice(0, 10);

const lineMatches = (line, prod) => {
  if (hasId(line && line.productId) && hasId(prod && prod.id)) return String(line.productId) === String(prod.id);
  return normProductName(line && (line.productName || line.name)) === normProductName(prod && (prod.productName || prod.name));
};

const saleItemsOf = (s) =>
  Array.isArray(s && s.items) && s.items.length > 0
    ? s.items
    : [{ productName: s && s.productName, quantity: s && s.quantity, productId: s && s.productId, cost: s && s.cost, sellingPrice: s && s.sellingPrice, type: s && s.isService ? 'service' : 'product' }];

const returnItemsOf = (r) =>
  Array.isArray(r && r.items) && r.items.length > 0
    ? r.items
    : [{ productName: r && r.productName, qty: (r && (r.qty != null ? r.qty : r.quantity)), productId: r && r.productId }];

// ── Cost exceptions — per non-service sale line ────────────────────────────
// recordedUnitCost = the frozen snapshot on the line; replayUnitCost = the
// perpetual WAC as of the sale date (what the line SHOULD have captured).
export const costExceptionRows = (inputs = {}, { from = null, to = null, tolerance = 1 } = {}) => {
  const { products = [], purchases = [], sales = [], salesReturns = [], purchaseReturns = [] } = inputs;
  const fromK = from ? dkey(from) : null, toK = to ? dkey(to) : null;
  const inRange = (d) => { const k = dkey(d); return (!fromK || k >= fromK) && (!toK || k <= toK); };
  const rows = [];
  (sales || []).filter((s) => !s.deleted && inRange(s.date)).forEach((s) => {
    saleItemsOf(s).forEach((it) => {
      if (it.type === 'service') return;
      const qty = num(it.quantity);
      if (qty <= 0) return;
      const prod = (products || []).find((p) => lineMatches(it, p));
      const recordedUnit = num(it.cost);
      // Replay WAC as of the sale date (falls back to opening/cost price inside computeWAC).
      const replayUnit = prod ? computeWAC(prod.productName, products, purchases, sales, salesReturns, purchaseReturns, prod, s.date) : 0;
      const recordedTotal = r2(recordedUnit * qty);
      const replayTotal = r2(replayUnit * qty);
      const difference = r2(replayTotal - recordedTotal);
      let reason = null, action = null;
      if (recordedUnit <= 0 && replayUnit > 0) {
        reason = 'zero_cost_snapshot';
        action = 'Backfill line cost to replay WAC (operator-confirmed); books false 100% margin until then.';
      } else if (Math.abs(difference) > tolerance) {
        reason = 'wac_variation';
        action = 'Review: snapshot taken at a different WAC than the chronological replay. Replay is the accounting-correct cost.';
      }
      if (!reason) return;
      rows.push({
        type: 'cost', reason, action,
        saleId: s.id, invoice: s.invoiceNumber || s.invoiceNo || null, date: dkey(s.date),
        product: (prod && prod.productName) || it.productName || it.name || '—',
        productId: prod ? prod.id : (it.productId ?? null),
        quantity: qty,
        recordedUnitCost: r2(recordedUnit), replayUnitCost: r2(replayUnit),
        recordedTotalCost: recordedTotal, replayTotalCost: replayTotal, difference,
      });
    });
  });
  return rows.sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference));
};

// ── Return-integrity exceptions ────────────────────────────────────────────
// Compares each customer return against its parent sale line: is the parent
// present, is the return dated on/after the sale, and does the cumulative
// returned quantity stay within what was sold.
export const returnExceptionRows = (inputs = {}) => {
  const { products = [], sales = [], salesReturns = [] } = inputs;
  const soldQtyOnSale = (sale, ref) => saleItemsOf(sale).reduce((a, it) => (it.type !== 'service' && lineMatches(it, ref)) ? a + num(it.quantity) : a, 0);
  const active = (salesReturns || []).filter((r) => !r.deleted);
  const rows = [];
  active.forEach((r) => {
    const parent = (sales || []).find((s) => String(s.id) === String(r.originalSaleId));
    returnItemsOf(r).forEach((it) => {
      const qty = num(it.qty != null ? it.qty : it.quantity);
      if (qty <= 0) return;
      const ref = (products || []).find((p) => lineMatches(it, p)) || it;
      const productName = (ref && (ref.productName || ref.name)) || it.productName || '—';
      // prior returns of the SAME sale+product, strictly before this one (by date then id)
      const priorReturned = active.filter((o) => o !== r && String(o.originalSaleId) === String(r.originalSaleId))
        .filter((o) => (dkey(o.date) < dkey(r.date)) || (dkey(o.date) === dkey(r.date) && String(o.id) < String(r.id)))
        .reduce((a, o) => a + returnItemsOf(o).filter((x) => lineMatches(x, ref)).reduce((b, x) => b + num(x.qty != null ? x.qty : x.quantity), 0), 0);
      const parentSoldQty = parent ? soldQtyOnSale(parent, ref) : null;
      const maxReturnable = parentSoldQty != null ? r2(parentSoldQty - priorReturned) : null;
      let reason = null, action = null;
      if (!parent) {
        reason = 'orphan_return';
        action = 'Parent sale not found — quarantine or re-link the return; it is excluded from valid calculations.';
      } else if (dkey(r.date) < dkey(parent.date)) {
        reason = 'return_before_sale';
        action = 'Return is dated before its own sale — correct the return date (or void and re-record); it corrupts chronological replay ordering.';
      } else if (parentSoldQty != null && qty > maxReturnable + 0.0001) {
        reason = 'over_return';
        action = 'Returned quantity exceeds what is still returnable on the parent sale — reduce or void this return.';
      }
      if (!reason) return;
      rows.push({
        type: 'return', reason, action,
        returnId: r.id, originalSaleId: r.originalSaleId, date: dkey(r.date),
        parentSaleDate: parent ? dkey(parent.date) : null,
        product: productName, productId: (ref && ref.id) ?? (it.productId ?? null),
        returnQuantity: qty, parentSoldQty, priorReturned: r2(priorReturned), maxReturnable,
        refundAmt: num(r.refundAmt != null ? r.refundAmt : r.totalRefund),
      });
    });
  });
  return rows;
};

// ── Stock exceptions — stored balance vs replay quantity (reuses inventory) ─
export const stockExceptionRows = (inputs = {}) => {
  const rec = reconcileProductStock({
    products: inputs.products || [], purchases: inputs.purchases || [], sales: inputs.sales || [],
    salesReturns: inputs.salesReturns || [], purchaseReturns: inputs.purchaseReturns || [],
    stockAdjustments: inputs.stockAdjustments || [],
  });
  return rec.filter((r) => r.status !== 'ok').map((r) => ({
    type: 'stock',
    reason: r.status === 'unresolved_legacy' ? 'ambiguous_identity' : 'stock_mismatch',
    action: r.status === 'unresolved_legacy'
      ? 'Duplicate normalized name — resolve product identity before trusting the replay quantity.'
      : 'Investigate the movement history (often an invalid return); correct the source, then re-check. Do NOT blind-rebuild.',
    product: r.productName, productId: r.productId,
    storedStock: r2(r.storedQty), replayStock: r2(r.replayQty), stockDifference: r2(r.difference),
  }));
};

// ── One combined report ────────────────────────────────────────────────────
export const historicalCostStockExceptions = (inputs = {}, opts = {}) => {
  const cost = costExceptionRows(inputs, opts);
  const returns = returnExceptionRows(inputs);
  const stock = stockExceptionRows(inputs);
  const rows = [...cost, ...returns, ...stock];
  const summary = {
    costLines: cost.length,
    zeroCostLines: cost.filter((r) => r.reason === 'zero_cost_snapshot').length,
    wacVariationLines: cost.filter((r) => r.reason === 'wac_variation').length,
    totalCostUnderstatement: r2(cost.reduce((s, r) => s + r.difference, 0)),
    returnExceptions: returns.length,
    stockExceptions: stock.length,
  };
  return { rows, cost, returns, stock, summary };
};

// ── DUPLICATE INVOICE-NUMBER DETECTION ─────────────────────────────────────
// Two issued documents sharing an invoice number is an integrity collision
// (typically two devices issuing offline). Detect it; never silently renumber.
// Returns one row per colliding number: { invoiceNumber, ids, count }.
export const duplicateInvoiceNumbers = (records = []) => {
  const seen = {};
  (records || []).forEach((r) => {
    if (r && r.deleted) return;
    const n = r && r.invoiceNumber;
    if (n == null || n === '') return;
    (seen[n] = seen[n] || []).push(r.id);
  });
  return Object.keys(seen)
    .filter((n) => seen[n].length > 1)
    .map((n) => ({ invoiceNumber: n, ids: seen[n], count: seen[n].length }));
};

// ── PER-PRODUCT TRANSACTION TIMELINE ───────────────────────────────────────
// The chronological ledger for ONE product: opening, purchases, sales, customer
// & supplier returns, and stock adjustments — each with the quantity change, the
// running quantity, and a running WAC/value — so the operator can SEE where the
// replay and the stored balance diverge. Sales also carry their snapshot cost so
// a zero-cost row is obvious. Returns dated before their parent sale are flagged
// inline. Running quantity includes stock adjustments so the closing matches the
// stock reconciliation's replay quantity.
export const productTimeline = (productName, inputs = {}) => {
  const { products = [], purchases = [], sales = [], salesReturns = [], purchaseReturns = [], stockAdjustments = [] } = inputs;
  const tgt = normProductName(productName);
  const prod = (products || []).find((p) => normProductName(p.productName || p.name) === tgt);
  const pid = prod && hasId(prod.id) ? String(prod.id) : null;
  const m = (it) => (pid != null && it && hasId(it.productId)) ? String(it.productId) === pid : normProductName(it && (it.productName || it.name)) === tgt;
  const saleDateById = {};
  (sales || []).forEach((s) => { saleDateById[String(s.id)] = dkey(s.date); });

  const ev = [];
  (purchases || []).forEach((pu) => {
    const items = Array.isArray(pu.items) && pu.items.length ? pu.items : [{ productName: pu.productName, quantity: pu.quantity, productId: pu.productId, totalCost: pu.totalCost, unitPrice: pu.unitPrice }];
    items.forEach((it) => { if (!m(it)) return; const q = num(it.quantity); const c = num(it.totalCost || q * num(it.unitPrice || it.unitCost)); if (q > 0) ev.push({ date: dkey(pu.date), t: 0, kind: 'purchase', qty: q, cost: c, ref: pu.invoiceNumber || pu.efdReceiptNo || pu.supplier || null }); });
  });
  (sales || []).forEach((s) => {
    if (s.deleted) return;
    const items = Array.isArray(s.items) && s.items.length ? s.items : [{ productName: s.productName, quantity: s.quantity, productId: s.productId, cost: s.cost, type: s.isService ? 'service' : 'product' }];
    items.forEach((it) => { if (it.type === 'service' || !m(it)) return; const q = num(it.quantity); if (q > 0) ev.push({ date: dkey(s.date), t: 1, kind: 'sale', qty: q, snapshotUnitCost: num(it.cost), ref: s.invoiceNumber || null, saleId: s.id }); });
  });
  (salesReturns || []).forEach((r) => {
    if (r.deleted) return;
    const items = Array.isArray(r.items) && r.items.length ? r.items : [{ productName: r.productName, qty: r.qty, productId: r.productId, unitCost: r.unitCost }];
    items.forEach((it) => { if (!m(it)) return; const q = num(it.qty != null ? it.qty : it.quantity); const snap = it.unitCost != null && it.unitCost !== '' ? num(it.unitCost) : null; if (q > 0) { const parentDate = saleDateById[String(r.originalSaleId)] || null; ev.push({ date: dkey(r.date), t: 2, kind: 'customer_return', qty: q, cost: snap, ref: r.id, parentSaleDate: parentDate, impossibleDate: parentDate ? dkey(r.date) < parentDate : false }); } });
  });
  (purchaseReturns || []).forEach((r) => {
    if (r.deleted) return;
    const items = Array.isArray(r.items) && r.items.length ? r.items : [{ productName: r.productName, qty: r.qty, productId: r.productId }];
    items.forEach((it) => { if (!m(it)) return; const q = num(it.qty != null ? it.qty : it.quantity); if (q > 0) ev.push({ date: dkey(r.date), t: 3, kind: 'supplier_return', qty: q, ref: r.id }); });
  });
  (stockAdjustments || []).forEach((a) => {
    if (a.deleted || !m(a)) return;
    const q = num(a.qty != null ? a.qty : a.quantity);
    const signed = (a.direction === 'decrease' || a.type === 'decrease') ? -Math.abs(q) : Math.abs(q);
    if (q !== 0) ev.push({ date: dkey(a.date), t: 4, kind: 'adjustment', qty: signed, ref: a.reason || a.note || null });
  });
  ev.sort((a, b) => (new Date(a.date || 0) - new Date(b.date || 0)) || (a.t - b.t));

  let qty = num(prod && prod.openingStockQty);
  let value = num(prod && prod.openingStockValue);
  let lastWAC = qty > 0 ? value / qty : num(prod && prod.costPrice);
  const rows = [{
    date: null, kind: 'opening', label: 'Opening stock', qtyChange: qty,
    runningQty: qty, unitCost: qty > 0 ? r2(value / qty) : r2(lastWAC), runningWAC: qty > 0 ? r2(value / qty) : r2(lastWAC), runningValue: r2(value), ref: null,
  }];
  for (const e of ev) {
    const wac = qty > 0 ? value / qty : lastWAC;
    let qtyChange = 0; const row = { date: e.date, kind: e.kind, ref: e.ref };
    if (e.kind === 'purchase') { value += e.cost; qty += e.qty; qtyChange = e.qty; row.unitCost = r2(e.cost / e.qty); }
    else if (e.kind === 'customer_return') { const c = e.cost != null ? e.cost : wac; value += e.qty * c; qty += e.qty; qtyChange = e.qty; row.unitCost = r2(c); row.parentSaleDate = e.parentSaleDate; row.impossibleDate = e.impossibleDate; }
    else if (e.kind === 'sale') { const out = Math.min(e.qty, qty); value -= out * wac; qty -= e.qty; qtyChange = -e.qty; row.unitCost = r2(wac); row.snapshotUnitCost = r2(e.snapshotUnitCost); row.zeroCost = e.snapshotUnitCost <= 0; row.saleId = e.saleId; }
    else if (e.kind === 'supplier_return') { const out = Math.min(e.qty, qty); value -= out * wac; qty -= e.qty; qtyChange = -e.qty; row.unitCost = r2(wac); }
    else if (e.kind === 'adjustment') { value += e.qty * wac; qty += e.qty; qtyChange = e.qty; row.unitCost = r2(wac); }
    if (qty > 0) lastWAC = value / qty;
    row.qtyChange = qtyChange; row.runningQty = r2(qty); row.runningWAC = r2(qty > 0 ? value / qty : lastWAC); row.runningValue = r2(value);
    rows.push(row);
  }
  const storedQty = num(prod && (prod.balance != null ? prod.balance : (prod.currentStock != null ? prod.currentStock : prod.openingStockQty)));
  return {
    productName: (prod && prod.productName) || productName,
    productId: prod ? prod.id : null,
    rows,
    replayQty: r2(qty), replayValue: r2(value), storedQty: r2(storedQty),
    difference: r2(storedQty - qty),
  };
};

// Per-sale replay unit cost map, keyed `${saleId}::${normProductName}`. The value
// is the PERPETUAL WAC at that sale in the SAME chronological replay the P&L uses
// (via productTimeline) — so the sales export's "Accounting Unit Cost" is drawn
// from the one deterministic engine, not a separate per-row WAC implementation.
export const replaySaleUnitCosts = (inputs = {}) => {
  const map = {};
  (inputs.products || []).forEach((p) => {
    const tl = productTimeline(p.productName, inputs);
    const key = normProductName(p.productName);
    tl.rows.forEach((r) => {
      if (r.kind === 'sale' && r.saleId != null) map[`${String(r.saleId)}::${key}`] = r.unitCost;
    });
  });
  return map;
};

// ── OPERATOR-APPROVED CORRECTIONS (preview → approve; originals retained) ───
// These build the corrected record arrays; they NEVER auto-apply. The caller
// (a confirm/preview UI) decides whether to commit them to state.

// Re-date an impossible-date return (return dated before its own sale) to a
// valid date (normally the parent sale date). Keeps the original date and a
// reason for audit; stamps updatedAt for record-level sync.
export const fixReturnDate = (salesReturns = [], returnId, newDate, {
  now = new Date().toISOString(),
  reason = 'Return dated before its parent sale; re-dated to the sale date.',
} = {}) => (salesReturns || []).map((r) => {
  if (String(r.id) !== String(returnId)) return r;
  return {
    ...r,
    date: dkey(newDate),
    originalReturnDate: r.originalReturnDate != null ? r.originalReturnDate : dkey(r.date),
    dateCorrectedAt: now,
    dateCorrectionReason: reason,
    updatedAt: now,
  };
});

// Backfill ONLY zero_cost_snapshot sale lines to their replay WAC. WAC-variation
// lines are deliberately left untouched. For each corrected line, retain the
// original recorded cost and stamp the correction; recompute the sale's total
// cost/profit incrementally (the old zero contribution → the corrected cost).
export const backfillZeroCostLines = (sales = [], costRows = [], {
  now = new Date().toISOString(),
  source = 'replay_wac',
  reason = 'Historical sale recorded with zero COGS despite an available opening/WAC cost.',
} = {}) => {
  const fixes = (costRows || []).filter((r) => r.reason === 'zero_cost_snapshot' && num(r.replayUnitCost) > 0);
  if (fixes.length === 0) return sales;
  const bySale = {};
  fixes.forEach((f) => { (bySale[String(f.saleId)] = bySale[String(f.saleId)] || []).push(f); });
  const matchFix = (list, it) => list.find((f) =>
    (hasId(f.productId) && hasId(it.productId) && String(f.productId) === String(it.productId)) ||
    normProductName(f.product) === normProductName(it.productName || it.name));
  return (sales || []).map((s) => {
    const list = bySale[String(s.id)];
    if (!list) return s;
    const explicit = Array.isArray(s.items) && s.items.length > 0;
    const items = explicit ? s.items
      : [{ productName: s.productName, quantity: s.quantity, productId: s.productId, cost: s.cost, sellingPrice: s.sellingPrice, type: s.isService ? 'service' : 'product' }];
    let addedCost = 0, changed = false;
    const newItems = items.map((it) => {
      if (it.type === 'service') return it;
      if (num(it.cost) > 0) return it; // already costed — leave it
      const f = matchFix(list, it);
      if (!f) return it;
      const qty = num(it.quantity);
      const unit = num(f.replayUnitCost);
      addedCost += qty * unit;
      changed = true;
      return {
        ...it,
        cost: String(unit),
        unitCost: String(unit), // keep both cost fields in sync for readers of either
        // Preserve-if-present: the ORIGINAL recorded cost must survive permanently,
        // never be overwritten by an already-corrected value on any re-run.
        originalRecordedUnitCost: it.originalRecordedUnitCost != null ? it.originalRecordedUnitCost : (it.cost != null ? String(it.cost) : '0'),
        originalRecordedTotalCost: it.originalRecordedTotalCost != null ? it.originalRecordedTotalCost : String(r2(num(it.cost) * qty)),
        correctedUnitCost: String(unit),
        correctedTotalCost: String(r2(unit * qty)),
        costCorrectedAt: now,
        costCorrectionReason: reason,
        costCorrectionSource: source,
      };
    });
    if (!changed) return s;
    const newTotalCost = r2(num(s.totalCost) + addedCost); // old zero contribution → corrected
    const newProfit = r2(num(s.totalRevenue) - newTotalCost);
    // Keep the backward-compat top-level `cost` (first item) consistent too, so
    // legacy detail screens / older exports don't show a stale zero.
    const firstCost = newItems[0] && newItems[0].cost;
    return {
      ...s,
      ...(explicit ? { items: newItems, ...(firstCost != null ? { cost: String(firstCost) } : {}) }
                   : { cost: firstCost }),
      originalRecordedTotalCost: s.originalRecordedTotalCost != null ? s.originalRecordedTotalCost : String(num(s.totalCost)),
      originalTotalCost: s.originalTotalCost != null ? s.originalTotalCost : String(num(s.totalCost)),
      totalCost: String(newTotalCost),
      profit: newProfit,
      costCorrectedAt: now,
      updatedAt: now,
    };
  });
};

// CSV for the operator (one combined sheet). Blank cells where a column does not
// apply to that row type — the report is intentionally denormalized.
export const exceptionsToCSV = (report) => {
  const cols = ['type', 'reason', 'invoice', 'date', 'product', 'quantity',
    'recordedUnitCost', 'replayUnitCost', 'recordedTotalCost', 'replayTotalCost', 'difference',
    'returnQuantity', 'parentSoldQty', 'maxReturnable', 'parentSaleDate',
    'storedStock', 'replayStock', 'stockDifference', 'action'];
  const esc = (v) => { if (v == null) return ''; const s = String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const head = cols.join(',');
  const body = (report.rows || []).map((row) => cols.map((c) => esc(row[c])).join(',')).join('\n');
  return `${head}\n${body}`;
};
